import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/auth.service';
import {
  AuthState,
  User,
  LoginRequest,
  RegisterRequest,
  ChangePasswordRequest,
  ImpersonationTargetUser,
} from '../../types/auth.types';
import { ApiError } from '@/utils/api-response.util';
import { RootState } from '../index';
import { clearClientFeatureCache } from '@/hooks/useClientPlan';

// --- Initial State ---
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false, // Загальний індикатор завантаження
  initialLoading: true, // Тільки для первинної перевірки автентифікації
  error: null,
  isImpersonating: false,
  originalUser: null,
  impersonationError: null,
};

// --- Async Thunks ---

export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (_, { rejectWithValue }) => {
    try {
      if (!authService.isAuthenticated()) {
        return rejectWithValue({ message: 'No token', clearAuth: true });
      }
      const currentUser = await authService.getCurrentUser();
      clearClientFeatureCache(); // new session (or first load) — force a fresh fetch, not a stale/previous user's cache
      if (authService.isImpersonating()) {
        const adminUserStr = sessionStorage.getItem('impersonationAdminUser');
        const originalUser = adminUserStr ? JSON.parse(adminUserStr) as User : null;
        return { user: currentUser, originalUser, isImpersonating: true };
      }
      return { user: currentUser, originalUser: null, isImpersonating: false };
    } catch (error: any) {
      authService.logout();
      return rejectWithValue({ message: 'Invalid session', clearAuth: true });
    }
  }
);

export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      await authService.login(credentials);
      const user = await authService.getCurrentUser();
      clearClientFeatureCache(); // new session — force a fresh fetch, not a previous user's cache
      return user;
    } catch (error: any) {
      const apiError = error as ApiError;
      return rejectWithValue(apiError.message || 'Помилка входу');
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData: RegisterRequest, { rejectWithValue }) => {
    try {
      await authService.register(userData);
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      const apiError = error as ApiError;
      return rejectWithValue(apiError.message || 'Помилка реєстрації');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      const apiError = error as ApiError;
      return rejectWithValue(apiError.message || 'Помилка завантаження користувача');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      clearClientFeatureCache();
    } catch (error: any) {
      const apiError = error as ApiError;
      return rejectWithValue(apiError.message || 'Помилка виходу');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: ChangePasswordRequest, { rejectWithValue }) => {
    try {
      await authService.changePassword(data);
    } catch (error: any) {
      const apiError = error as ApiError;
      return rejectWithValue(apiError.message || 'Помилка зміни паролю');
    }
  }
);

export const startImpersonation = createAsyncThunk(
  'auth/startImpersonation',
  async (userId: number, { getState, rejectWithValue }) => {
    try {
      const { user: adminUser } = (getState() as { auth: AuthState }).auth;
      if (!adminUser) return rejectWithValue('Admin user not found');

      // Save admin user for recovery on page reload
      sessionStorage.setItem('impersonationAdminUser', JSON.stringify(adminUser));

      const data = await authService.impersonate(userId);

      // Switch sidebar mode to match target user's role
      const targetRole = data.targetUser.roles[0];
      if (targetRole === 'client' || targetRole === 'expert') {
        localStorage.setItem('sidebar-mode', targetRole);
      }

      return { targetUser: data.targetUser, originalUser: adminUser };
    } catch (error: any) {
      const apiError = error as ApiError;
      authService.clearImpersonationData();
      return rejectWithValue(apiError.message || 'Помилка входу від імені користувача');
    }
  }
);

export const stopImpersonation = createAsyncThunk(
  'auth/stopImpersonation',
  async (_, { getState, rejectWithValue }) => {
    try {
      const sessionId = authService.getImpersonationSessionId();

      // Clear impersonation data first (admin token takes over in getAccessToken())
      authService.clearImpersonationData();

      // Revoke session on backend (fire-and-forget)
      if (sessionId) {
        authService.revokeImpersonationSession(sessionId).catch(() => {});
      }

      // Restore admin sidebar mode
      localStorage.setItem('sidebar-mode', 'admin');

      const { originalUser } = (getState() as { auth: AuthState }).auth;
      return originalUser;
    } catch {
      return rejectWithValue('Failed to stop impersonation');
    }
  }
);

// --- Slice Definition ---

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.impersonationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuth.pending, (state) => {
        state.initialLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.isImpersonating = action.payload.isImpersonating;
        state.originalUser = action.payload.originalUser;
        state.initialLoading = false;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isAuthenticated = false;
        state.user = null;
        state.initialLoading = false;
        state.error = (action.payload as any)?.message;
      })

      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Logout
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        Object.assign(state, initialState, { loading: false, initialLoading: false });
      })
      .addCase(logoutUser.rejected, (state) => {
        Object.assign(state, initialState, { loading: false, initialLoading: false });
      })

      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Start Impersonation
      .addCase(startImpersonation.pending, (state) => {
        state.loading = true;
        state.impersonationError = null;
      })
      .addCase(startImpersonation.fulfilled, (state, action) => {
        state.loading = false;
        state.isImpersonating = true;
        // Build a minimal User from targetUser (full profile loaded by checkAuth on next render)
        const t = action.payload.targetUser as ImpersonationTargetUser;
        state.user = {
          id: t.id, name: t.name, surname: t.surname, email: t.email,
          roles: t.roles, avatar: t.avatar ?? undefined,
          permissions: [], isEmailVerified: false, isFirstSurveyCompleted: false,
          isActive: true, lastLoginAt: '', createdAt: '',
        };
        state.originalUser = action.payload.originalUser;
      })
      .addCase(startImpersonation.rejected, (state, action) => {
        state.loading = false;
        state.impersonationError = action.payload as string;
      })

      // Stop Impersonation
      .addCase(stopImpersonation.pending, (state) => {
        state.loading = true;
      })
      .addCase(stopImpersonation.fulfilled, (state, action: PayloadAction<User | null>) => {
        state.loading = false;
        state.isImpersonating = false;
        state.user = action.payload;
        state.originalUser = null;
      })
      .addCase(stopImpersonation.rejected, (state, action) => {
        state.loading = false;
        state.impersonationError = action.payload as string;
      });
  },
});

export const { clearError } = authSlice.actions;

// Selectors
export const selectUser = (state: RootState) => state.auth.user;

export default authSlice.reducer;
